/**
 * Every read the console makes, in one place — the mirror image of
 * `worker/catalog.ts`.
 *
 * Screens used to build their requests inside `useQuery` closures, which made
 * the URLs untestable: a render test seeds the cache, so the closure never
 * runs, and a mistyped path would pass every render test and every browser
 * walkthrough before answering NOT_IN_CATALOG in production. Query options live
 * here instead so `app/api-requests.test.ts` can execute them, assert the exact
 * URL and params, and check both against the catalog that has to forward them.
 *
 * Params are ProductData's own names. The mcp-admin catalog's `limit`/`sortBy`/
 * `query` spellings are Vercel-era aliases the data plane ignores.
 */
import { apiGet } from "@/lib/api";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  CompanyShareholdersResponse,
  DashboardData,
  FaxListExtras,
  FaxRow,
  FederalTaxDeadlinesResponse,
  FederalTaxDetail,
  FederalTaxQueueRow,
  FederalTaxStats,
  FederalTaxTriageResponse,
  FormationDetailData,
  FormationRow,
  ListResponse,
  MailItemRow,
  MailroomListResponse,
  OrderRow,
  OrderTabCounts,
  CompanyServiceDetail,
  CompanyServiceListResponse,
  WorkQueueResponse,
} from "@/types";

/** Rows per page, shared by every list screen (standard: 20). */
const PAGE_SIZE = 20;

// --- the work queue (the console landing) ------------------------------------

/** The admin work queue's page size, updated to 20 rows. */
const WORK_QUEUE_PAGE_SIZE = 20;

export interface WorkQueueListParams {
  page: number;
  search: string;
  /** Which view: orders and services, or inbound faxes waiting for a company. */
  kind: "order" | "fax";
}

/**
 * What the console lands on with no page and no search — page one, unfiltered.
 *
 * Named rather than spelled out at each call site because the sidebar's
 * new-orders badge asks for EXACTLY this, which is the whole reason the badge
 * no longer costs a second read of the same handler (see
 * `app/hooks/use-new-orders-count.ts`).
 */
export const WORK_QUEUE_LANDING: WorkQueueListParams = {
  page: 1,
  search: "",
  kind: "order",
};

export function workQueueListQuery(params: WorkQueueListParams) {
  return {
    ...LIVE_LIST,
    queryKey: ["work-queue", params.kind, params.page, params.search] as const,
    queryFn: () =>
      apiGet<WorkQueueResponse>("/api/admin/orders/work-queue", {
        page: params.page,
        pageSize: WORK_QUEUE_PAGE_SIZE,
        search: params.search || undefined,
        kind: params.kind,
      }),
  };
}

// --- dashboard and orders (wave 1) -------------------------------------------

/**
 * The dashboard aggregate — the heaviest read the console makes.
 *
 * Upstream counts users, companies, orders, subscriptions and settled payments
 * in SQL and returns one payload (`dashboard/admin.ts`), which is why it is an
 * AGGREGATE rather than a live list: every remount used to refetch all five
 * tables. Unenveloped upstream (`c.json(payload)` with no `data` key), which
 * `apiGet` passes through untouched.
 */
export function dashboardQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["dashboard"] as const,
    queryFn: () => apiGet<DashboardData>("/api/admin/dashboard"),
  };
}

/** Rows per page for orders list. */
const ORDERS_PAGE_SIZE = 20;

export interface OrdersListParams {
  page: number;
  search: string;
  status: string;
  /** The stalled-paid backlog: paid orders past ProductData's delivery SLA. */
  undeliveredPaid: boolean;
}

/**
 * The orders list.
 *
 * `undelivered_paid` REPLACES the status filter rather than narrowing it. The
 * backlog is a different question — "what did a customer pay for and not
 * receive", across every service type and every open status — and upstream
 * ANDs every clause, so sending both would answer neither. The old admin made
 * the same call, swapping its tabs out for the toggle.
 */
export function ordersListQuery(params: OrdersListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "orders",
      params.page,
      params.search,
      params.status,
      params.undeliveredPaid,
    ] as const,
    queryFn: () =>
      apiGet<ListResponse<OrderRow>>("/api/admin/orders", {
        page: params.page,
        pageSize: ORDERS_PAGE_SIZE,
        ...(params.undeliveredPaid
          ? { undelivered_paid: "true" }
          : {
              search: params.search || undefined,
              status: params.status || undefined,
            }),
      }),
  };
}

/** The counts behind the Orders tabs. Two grouped scans, so an aggregate. */
export function orderTabCountsQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["orders", "tab-counts"] as const,
    queryFn: () => apiGet<OrderTabCounts>("/api/admin/orders/tab-counts"),
  };
}

export function orderQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["order", id] as const,
    queryFn: () =>
      apiGet<OrderRow>(`/api/admin/orders/${encodeURIComponent(id)}`, {
        include_documents: "true",
      }),
  };
}

// --- federal tax, state tax, formation, fax (wave 4) -------------------------

/**
 * ProductData's federal-tax queue honours `sort_by=updated_at` and falls back
 * to `created_at` for every other value, so those two columns are the whole
 * offerable set — a "by package" sort would render as a control that quietly
 * does nothing.
 */
export const FEDERAL_TAX_SORTS: Record<
  string,
  { sort_by: string; sort_order: string }
> = {
  newest: { sort_by: "created_at", sort_order: "desc" },
  oldest: { sort_by: "created_at", sort_order: "asc" },
  updated: { sort_by: "updated_at", sort_order: "desc" },
};

export const FEDERAL_TAX_SORT_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "updated", label: "Recently updated" },
];

export interface FederalTaxQueueParams {
  page: number;
  status: string;
  taxYear: string;
  sort: string;
}

export function federalTaxQueueQuery(params: FederalTaxQueueParams) {
  const order = FEDERAL_TAX_SORTS[params.sort] ?? FEDERAL_TAX_SORTS.newest!;
  return {
    ...LIVE_LIST,
    queryKey: [
      "federal-tax-queue",
      params.page,
      params.status,
      params.taxYear,
      params.sort,
    ] as const,
    queryFn: () =>
      apiGet<ListResponse<FederalTaxQueueRow>>("/api/admin/federal-tax/queue", {
        page: params.page,
        pageSize: PAGE_SIZE,
        status: params.status || undefined,
        tax_year: params.taxYear || undefined,
        sort_by: order.sort_by,
        sort_order: order.sort_order,
      }),
  };
}

export function federalTaxStatsQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["federal-tax-stats"] as const,
    queryFn: () =>
      apiGet<FederalTaxStats>("/api/admin/federal-tax/filing/stats"),
  };
}

/**
 * The tabs upstream counts, in the order an operator works them (spec
 * 2026-09-26 §10.2). Each is a set of `nextAction` values (§8), decided
 * upstream in the triage SQL — the console only names the tab:
 * review_signatures ← review_signatures; forms_to_review ← review_packet,
 * start_packet_build (the build gate, spec 2026-10-01: every build waits for the
 * team's review, and a stale submitted order needs building by hand);
 * fax_failed ← fax_failed;
 * to_mail ← print_and_mail; fill_errors ← fix_fill_errors;
 * waiting_on_customer ← await_customer_data, await_customer_signature,
 * await_customer_mailing; extension_proofs ← review_extension_proof;
 * prepare_by_hand ← await_manual (a return the Worker does not build);
 * on_hold ← resolve_hold;
 * filed ← mark_complete (a sending filing with every packet out included),
 * done (status filed or completed); all ← every row.
 * needs_us ← every action that is ours to take now (the sidebar badge's list):
 * review_signatures, review_packet, start_packet_build, fax_failed,
 * print_and_mail, fix_fill_errors, review_extension_proof, await_manual,
 * resolve_hold, mark_complete; test companies left out. It is the default tab.
 * fee_gaps is the one tab that is not a `nextAction` bucket: every live filing
 * with an open late-fee gap from a rejected Form 7004 proof, whatever its
 * next action is.
 */
export const FEDERAL_TAX_TRIAGE_TABS: Array<{ key: string; label: string }> = [
  { key: "needs_us", label: "Needs us" },
  { key: "review_signatures", label: "Review signatures" },
  { key: "forms_to_review", label: "Forms to review" },
  { key: "fax_failed", label: "Fax failed" },
  { key: "to_mail", label: "To mail" },
  { key: "fill_errors", label: "Fill errors" },
  { key: "waiting_on_customer", label: "Waiting on customer" },
  { key: "extension_proofs", label: "Extension proofs" },
  { key: "prepare_by_hand", label: "Prepare by hand" },
  { key: "on_hold", label: "On hold" },
  { key: "fee_gaps", label: "Fee gaps" },
  { key: "filed", label: "Filed" },
  { key: "all", label: "All" },
];

export interface FederalTaxTriageParams {
  page: number;
  tab: string;
  search: string;
}

/**
 * The same queue path, asked for the tax team's worklist.
 *
 * `view=triage` selects a different handler upstream: it computes each row's
 * next action, customer progress, document status and deadline in SQL over
 * every row, and answers `tabCounts` beside them. Its page size is `limit` —
 * the triage branch never reads `pageSize`, so sending one would be a control
 * that silently does nothing.
 */
export function federalTaxTriageQuery(params: FederalTaxTriageParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "federal-tax-triage",
      params.page,
      params.tab,
      params.search,
    ] as const,
    queryFn: () =>
      apiGet<FederalTaxTriageResponse>("/api/admin/federal-tax/queue", {
        view: "triage",
        tab: params.tab,
        search: params.search || undefined,
        page: params.page,
        limit: PAGE_SIZE,
      }),
  };
}

/**
 * Every open filing's due date and days remaining, most urgent first.
 *
 * One SELECT upstream over all federal-tax orders with the final statuses
 * excluded. `companyId` narrows that to one company — the company panel's
 * deadlines come from here too, so no screen computes a due date of its own.
 */
export function federalTaxDeadlinesQuery(params: { companyId?: string } = {}) {
  return {
    ...AGGREGATE,
    queryKey: ["federal-tax-deadlines", params.companyId ?? null] as const,
    queryFn: () =>
      apiGet<FederalTaxDeadlinesResponse>(
        "/api/admin/federal-tax/filing/deadlines",
        params.companyId ? { companyId: params.companyId } : undefined,
      ),
  };
}

/**
 * The one admin GET upstream answers UNENVELOPED (`c.json(detail)`); `apiGet`
 * unwraps only when a `data` key is present, so the detail arrives whole.
 */
export function federalTaxOrderQuery(orderId: string) {
  return {
    ...DETAIL,
    queryKey: ["federal-tax-order", orderId] as const,
    queryFn: () =>
      apiGet<FederalTaxDetail>(
        `/api/admin/federal-tax/${encodeURIComponent(orderId)}`,
      ),
  };
}

export interface FormationListParams {
  page: number;
  /** A tab of the Formation list; the API owns which stages it holds. */
  stageGroup: string;
  formationState: string;
  companyId: string;
  search: string;
  /** `paid` keeps only the orders whose money we still hold; omitted lists every order. */
  paymentScope?: "paid";
}

export function formationListQuery(params: FormationListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "formation",
      params.page,
      params.stageGroup,
      params.formationState,
      params.companyId,
      params.search,
      params.paymentScope ?? "",
    ] as const,
    queryFn: () =>
      apiGet<ListResponse<FormationRow>>("/api/admin/formation", {
        page: params.page,
        pageSize: PAGE_SIZE,
        stage_group: params.stageGroup || undefined,
        formation_state: params.formationState || undefined,
        company_id: params.companyId || undefined,
        search: params.search || undefined,
        payment_scope: params.paymentScope,
      }),
  };
}

/**
 * `/api/admin/formation/:id` — the formation order's own record: its stage,
 * its EIN sub-workflow, its flags, and the five company columns upstream joins
 * in. Everything else the detail screen renders comes from the two reads below,
 * both of which the console already catalogued for other screens.
 */
export function formationDetailQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["formation-detail", id] as const,
    queryFn: () =>
      apiGet<FormationDetailData>(
        `/api/admin/formation/${encodeURIComponent(id)}`,
      ),
  };
}

/**
 * The order behind a formation — where its payment status, its order status,
 * its documents and the company's formation date and filing id actually live.
 *
 * The key is `["order", id]` ON PURPOSE: `routes/OrderDetail.tsx` builds the
 * same request inline under that key, so opening a formation detail warms the
 * order detail and vice versa. A different key would cost the same round trip
 * and warm nothing.
 */
export function orderWithDocumentsQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["order", id] as const,
    queryFn: () =>
      apiGet<OrderRow>(`/api/admin/orders/${encodeURIComponent(id)}`, {
        include_documents: "true",
      }),
  };
}

// --- company services: registered agent and mailroom ------------------------

export interface CompanyServicesListParams {
  page: number;
  status?: string;
  search?: string;
}

/**
 * The registered agent screen's list. Registered agent is a company SERVICE
 * (`company_services`), not an order, so this reads the services route filtered
 * to that type; the old order-keyed `/registered-agent` reads are gone from
 * the catalog. The API Worker honours `status` and `search` here, so the screen
 * has real filters.
 */
export function registeredAgentListQuery(params: CompanyServicesListParams) {
  return companyServicesListQuery("registered_agent", params);
}

/** The Mailroom screen's Services tab: the same read, filtered to mailroom services. */
export function mailroomServicesListQuery(params: CompanyServicesListParams) {
  return companyServicesListQuery("mailroom", params);
}

function companyServicesListQuery(
  serviceType: "registered_agent" | "mailroom",
  params: CompanyServicesListParams,
) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "company-services",
      serviceType,
      params.page,
      params.status ?? "",
      params.search ?? "",
    ] as const,
    queryFn: () =>
      apiGet<CompanyServiceListResponse>("/api/admin/company-services", {
        serviceType,
        status: params.status,
        search: params.search,
        page: params.page,
        pageSize: PAGE_SIZE,
      }),
  };
}

/**
 * One service with its history, by SERVICE id. This console has no `post()`;
 * the service is changed through the admin MCP server.
 */
export function registeredAgentDetailQuery(serviceId: string) {
  return {
    ...DETAIL,
    queryKey: ["company-service-detail", serviceId] as const,
    queryFn: () =>
      apiGet<CompanyServiceDetail>(
        `/api/admin/company-services/${encodeURIComponent(serviceId)}`,
      ),
  };
}

export interface FaxListParams {
  page: number;
  direction: string;
  status: string;
  documentType: string;
  companyId: string;
  search: string;
  /** Inbound faxes with media and no company yet (`unassigned=1`). */
  unassigned?: boolean;
}

export function faxListQuery(params: FaxListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "fax",
      params.page,
      params.direction,
      params.status,
      params.documentType,
      params.companyId,
      params.search,
      params.unassigned ?? false,
    ] as const,
    queryFn: () =>
      apiGet<ListResponse<FaxRow> & FaxListExtras>("/api/admin/fax", {
        page: params.page,
        pageSize: PAGE_SIZE,
        direction: params.direction || undefined,
        status: params.status || undefined,
        document_type: params.documentType || undefined,
        company_id: params.companyId || undefined,
        search: params.search || undefined,
        unassigned: params.unassigned ? 1 : undefined,
      }),
  };
}

export function faxQuery(faxId: string) {
  return {
    ...DETAIL,
    queryKey: ["fax-submission", faxId] as const,
    queryFn: () =>
      apiGet<FaxRow>(`/api/admin/fax/${encodeURIComponent(faxId)}`),
  };
}

// The wave-5 reads and the users/companies directory live in their own modules
// and are re-exported here, so `@/lib/queries` stays the one import a screen or
// a test reaches for.
export * from "./queries-directory";
export * from "./ein-corrections";
export * from "./queries-ops";

// The approval review reads (plan 05) live in their own module like the rest.
export * from "./queries-approvals";

// The partner program's reads (owner D19, 2026-10-14), same rule.
export * from "./queries-partners";

// The bookkeeping ledger's reads (plans 01-05), same rule.
export * from "./queries-accounting";
export * from "./queries-accounting-overview";

// The documents/mailroom/DocuSeal reads live in their own module and are
// re-exported here, so `@/lib/queries` stays the one import a screen or a
// test reaches for.
export * from "./queries-documents-mailroom";

// The cockpit's rules / chart / contacts / journals reads (plan 15), same rule.
export * from "./queries-cockpit";

// The Support area's reads (served over the SUPPORT RPC binding), same rule.
export * from "./queries-support";
